import React, { useState, useEffect } from 'react';
import type { ResponsivePreviewPanelProps } from './types';
import { usePreviewState } from './usePreviewState';
import { useBackendPreview } from './useBackendPreview';
import { PreviewHeader } from './PreviewHeader';
import { DeviceFrame } from './DeviceFrame';

const useIsMimicking = () => {
  const [isMimicking, setIsMimicking] = useState(false);
  useEffect(() => {
    const check = () => {
      setIsMimicking(
        !!localStorage.getItem('originalAdminToken') && !!localStorage.getItem('mimickedUser')
      );
    };
    check();
    window.addEventListener('storage', check);
    return () => window.removeEventListener('storage', check);
  }, []);
  return isMimicking;
};

export const ResponsivePreviewPanel: React.FC<ResponsivePreviewPanelProps> = ({
  siteId,
  pageId,
  isOpen,
  onClose,
  refreshTrigger = 0,
  components,
  externalPreview,
}) => {
  const isMimicking = useIsMimicking();
  const {
    iframeRef,
    containerRef,
    selectedDevice,
    isRotated,
    setIsRotated,
    handleDeviceChange,
    frameWidth,
    frameHeight,
    totalFrameWidth,
    totalFrameHeight,
    scale,
    isLoading: iframeLoading,
    handleRefresh: handleLegacyRefresh,
    handleIframeLoad,
  } = usePreviewState(isOpen, onClose, refreshTrigger);

  // When draft components are available, use the authoritative backend preview
  const useBackend = isOpen && Array.isArray(components) && !externalPreview;
  const {
    html: backendHtml,
    isLoading: backendLoading,
    error: backendError,
    refresh: backendRefresh,
  } = useBackendPreview(pageId, useBackend ? components : undefined, isOpen);

  if (!isOpen) return null;

  // Determine preview source: external > backend > legacy iframe
  const useExternal = !!externalPreview;
  const activeHtml = useExternal ? externalPreview!.html : useBackend ? backendHtml : null;
  const activeError = useExternal ? externalPreview!.error : useBackend ? backendError : null;
  const activeRefresh = useExternal
    ? externalPreview!.refresh
    : useBackend
      ? backendRefresh
      : handleLegacyRefresh;

  // Fallback URL used when backend HTML isn't available (legacy React route)
  const fallbackUrl = `/preview/${siteId}/${pageId}?embed=true`;

  // Composite loading
  const isLoading = useExternal
    ? externalPreview!.isLoading || iframeLoading
    : useBackend
      ? backendLoading || iframeLoading
      : iframeLoading;

  const handleRefresh = activeRefresh;

  return (
    <div
      className={`fixed ${isMimicking ? 'top-[52px]' : 'top-0'} right-0 bottom-0 left-0 z-[70] flex flex-col bg-gray-900`}
    >
      {/* Header */}
      <PreviewHeader
        frameWidth={frameWidth}
        frameHeight={frameHeight}
        scale={scale}
        selectedDevice={selectedDevice}
        isRotated={isRotated}
        isLoading={isLoading}
        onDeviceChange={handleDeviceChange}
        onRotate={() => setIsRotated(!isRotated)}
        onRefresh={handleRefresh}
        onClose={onClose}
      />

      {/* Preview Area */}
      <div
        ref={containerRef}
        className="flex-1 flex items-center justify-center overflow-hidden bg-gradient-to-br from-gray-800 to-gray-900"
      >
        {/* Error state */}
        {activeError && (
          <div className="absolute top-16 left-1/2 -translate-x-1/2 z-20 bg-red-900/90 text-red-100 px-4 py-2 rounded-lg text-sm max-w-md text-center">
            Preview error: {activeError}
            <button onClick={activeRefresh} className="ml-2 underline hover:text-white">
              Retry
            </button>
          </div>
        )}

        {/* Device Frame Container - scales to fit */}
        <div
          className="transition-transform duration-300"
          style={{
            transform: `scale(${scale})`,
            transformOrigin: 'center center',
          }}
        >
          <DeviceFrame
            selectedDevice={selectedDevice}
            isRotated={isRotated}
            frameWidth={frameWidth}
            frameHeight={frameHeight}
            totalFrameWidth={totalFrameWidth}
            totalFrameHeight={totalFrameHeight}
            isLoading={isLoading}
            previewUrl={fallbackUrl}
            srcdocHtml={activeHtml}
            siteId={siteId}
            pageId={pageId}
            iframeRef={iframeRef}
            onIframeLoad={handleIframeLoad}
          />
        </div>
      </div>

      {/* Footer hint */}
      <div className="px-4 py-2 bg-gray-800 border-t border-gray-700">
        <p className="text-xs text-gray-500 text-center">
          Press <kbd className="px-1.5 py-0.5 bg-gray-700 rounded text-gray-400 font-mono">Esc</kbd>{' '}
          to close
          {selectedDevice !== 'desktop' && (
            <span className="ml-2">• Click rotate to switch orientation</span>
          )}
          {(useBackend || useExternal) && <span className="ml-2">• Server-rendered preview</span>}
        </p>
      </div>
    </div>
  );
};

export default ResponsivePreviewPanel;
